import type { User } from "sdk";
import {
	createResource,
	createSignal,
	For,
	onCleanup,
	Show,
	type VoidProps,
} from "solid-js";
import { useApi } from "@/api";
import { useCtx } from "@/app/context";
import { useModals } from "@/contexts/modal";
import { flags } from "@/lib/flags";
import { Modal } from "@/modals/mod";

export function Authentication(props: VoidProps<{ user: User }>) {
	const [, modalctl] = useModals();
	const api = useApi();

	const sudoEnter = async () => {
		await api.client.http.POST("/api/v1/auth/sudo/upgrade", {});
	};

	// TODO: make enter sudo button exit sudo if you're in sudo mode
	const sudoExit = async () => {
		await api.client.http.DELETE("/api/v1/auth/sudo");
	};

	const isSudo = () => api.session()?.status === "Sudo";

	return (
		<div class="user-settings-auth">
			<h2>authentication</h2>
			<div>authentication settings, words go here, etc etc</div>
			<br />
			<h3>email addreses</h3>
			<Email user={props.user} />
			<h3>oauth</h3>
			<Oauth />
			<br />
			<Show when={flags.has("auth_totp")}>
				<h3>totp</h3>
				<div>todo</div>
				<br />
			</Show>
			<Show when={flags.has("auth_webauthn")}>
				<h3>webauthn</h3>
				<div>todo</div>
				<br />
			</Show>
			<div class="danger-zone">
				<h3 class="label">danger zone</h3>
				<div class="item">
					<Show when={!isSudo()}>
						<div class="info">
							<div class="title">enter sudo mode</div>
							<div class="description">
								sudo mode allows you to take dangerous actions on your account.
								in the future, sudo mode will be prompted for automatically.
							</div>
						</div>
						<button type="button" class="button danger" onClick={sudoEnter}>
							enter sudo mode
						</button>
					</Show>
					<Show when={isSudo()}>
						<div class="info">
							<div class="title">exit sudo mode</div>
							<div class="description">
								sudo mode allows you to take dangerous actions on your account.
								in the future, sudo mode will be prompted for automatically.
							</div>
						</div>
						<button type="button" class="button danger" onClick={sudoExit}>
							exit sudo mode
						</button>
					</Show>
				</div>
				<div class="item">
					<div class="info">
						<div class="title">change password</div>
						<div class="description">change your password</div>
					</div>
					<button
						type="button"
						class="button danger"
						classList={{ disabled: isSudo() }}
						data-tooltip={isSudo() ? "Requires sudo" : undefined}
						onClick={() => {
							modalctl.open({ type: "reset_password" });
						}}
					>
						change password
					</button>
				</div>
				<div class="item">
					<div class="info">
						<div class="title">disable account (TODO)</div>
						<div class="description">this will disable your account</div>
					</div>
					<button
						type="button"
						class="button danger"
						classList={{ disabled: isSudo() }}
						data-tooltip={isSudo() ? "Requires sudo" : undefined}
						onClick={() => alert("todo")}
					>
						disable
					</button>
				</div>
				<div class="item">
					<div class="info">
						<div class="title">delete account (TODO)</div>
						<div class="description">this will delete your account</div>
					</div>
					<button
						type="button"
						class="button danger"
						classList={{ disabled: isSudo() }}
						data-tooltip={isSudo() ? "Requires sudo" : undefined}
						onClick={() => alert("todo")}
					>
						self destruct
					</button>
				</div>
			</div>
		</div>
	);
}

function Email(_props: VoidProps<{ user: User }>) {
	const api = useApi();
	const [, modalctl] = useModals();

	// TODO: use props.user.emails when sync events are implemented
	const [emails, { refetch }] = createResource(async () => {
		const { data } = await api.client.http.GET("/api/v1/user/{user_id}/email", {
			params: { path: { user_id: "@self" } },
		});
		return data;
	});

	function addEmail() {
		modalctl.prompt("email?", (email: string | null) => {
			if (!email) return;
			api.client.http
				.PUT("/api/v1/user/{user_id}/email/{addr}", {
					params: { path: { user_id: "@self", addr: email } },
				})
				.then(refetch);
		});
	}

	function deleteEmail(email: string) {
		modalctl.confirm("delete email?", (conf: boolean) => {
			if (!conf) return;
			api.client.http
				.DELETE("/api/v1/user/{user_id}/email/{addr}", {
					params: { path: { user_id: "@self", addr: email } },
				})
				.then(refetch);
		});
	}

	function resendVerification(email: string) {
		api.client.http.POST(
			"/api/v1/user/{user_id}/email/{addr}/resend-verification",
			{
				params: { path: { user_id: "@self", addr: email } },
			},
		);
	}

	// TODO: button to make email primary

	return (
		<>
			<div class="email-list">
				<For each={emails()}>
					{(email) => (
						<div class="email">
							<div style="flex:1">
								<b>{email.email}</b>
								{email.is_verified ? " (verified)" : " (unverified)"}
								{email.is_primary && " (primary)"}
							</div>
							<menu>
								<Show when={!email.is_verified}>
									<button
										type="button"
										class="button"
										onClick={() => resendVerification(email.email)}
									>
										resend verification
									</button>
								</Show>
								<button
									type="button"
									class="button danger"
									onClick={() => deleteEmail(email.email)}
								>
									delete
								</button>
							</menu>
						</div>
					)}
				</For>
			</div>
			<div class="email-add">
				<button type="button" class="button primary" onClick={addEmail}>
					add email
				</button>
			</div>
		</>
	);
}

function Oauth() {
	const api = useApi();

	// TODO: dont use debug route for this
	// add something to sync i guess
	const [oauthProviders] = createResource(async () => {
		const { data } = await api.client.http.GET("/api/v1/debug/info");
		return data &&
			"features" in data &&
			data.features &&
			"oauth" in data.features &&
			data.features.oauth &&
			"providers" in data.features.oauth
			? data.features.oauth.providers
			: undefined;
	});

	const [enabledOauthProviders, { refetch: refetchOauthProviders }] =
		createResource(async () => {
			const { data } = await api.client.http.GET("/api/v1/auth/state", {});
			return (data as any)?.oauth_providers ?? [];
		});

	const connectOauth = async (id: string) => {
		const url = await api.auth.oauthUrl(id);
		const popup = globalThis.open(url, "oauth_popup");
		// NOTE: do i want to open as a window?
		// const popup = globalThis.open(url, "oauth_popup", "width=600,height=800");

		let removed = false;

		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== window.location.origin) return;
			if (event.data?.type === "oauth_success") {
				refetchOauthProviders();
				window.removeEventListener("message", handleMessage);
				removed = true;
			}
		};
		window.addEventListener("message", handleMessage);

		const checkPopupClosed = setInterval(() => {
			if (popup?.closed) {
				clearInterval(checkPopupClosed);
				refetchOauthProviders();
				window.removeEventListener("message", handleMessage);
				removed = true;
			}
		}, 500);

		// just in case, make sure event listeners are cleaned up
		onCleanup(() => {
			if (!removed) {
				console.warn("oauth listener wasn't removed");
				window.removeEventListener("message", handleMessage);
			}
		});
	};

	const disconnectOauth = async (id: string) => {
		await api.client.http.DELETE("/api/v1/auth/oauth/{provider}", {
			params: { path: { provider: id } },
		});
		refetchOauthProviders();
	};

	const isSudo = () => api.session()?.status === "Sudo";

	return (
		<div class="oauth">
			<For each={oauthProviders()}>
				{(provider) => {
					const connected = () =>
						enabledOauthProviders()?.includes(provider.id);
					const requiresSudo = () => connected() && !isSudo();

					return (
						<div class="provider">
							<div style="flex:1">{provider.name}</div>
							<button
								type="button"
								class="button"
								onClick={() =>
									connected()
										? disconnectOauth(provider.id)
										: connectOauth(provider.id)
								}
								classList={{ danger: connected(), disabled: requiresSudo() }}
								data-tooltip={requiresSudo() ? "Requires sudo" : undefined}
							>
								{connected() ? "disconnect" : "connect"}
							</button>
						</div>
					);
				}}
			</For>
		</div>
	);
}

export const ModalResetPassword = () => {
	const [password, setPassword] = createSignal("");
	const [confirmPassword, setConfirmPassword] = createSignal("");
	const ctx = useCtx();
	const [, modalctl] = useModals();

	async function handlePasswordSet(e: SubmitEvent) {
		e.preventDefault();

		if (!password()) {
			modalctl.alert("missing password");
			return;
		}
		if (!confirmPassword()) {
			modalctl.alert("missing confirmPassword");
			return;
		}

		if (password() !== confirmPassword()) {
			modalctl.alert("password !== confirmPassword");
			return;
		}

		ctx.client.http.PUT("/api/v1/auth/password", {
			body: { password: password() },
		});
	}

	return (
		<Modal>
			<div class="auth">
				<section class="form-wrapper">
					reset password
					<form onSubmit={handlePasswordSet}>
						<label>
							<div class="label-text">password</div>
							<input
								class="input"
								type="password"
								placeholder="dolphins"
								value={password()}
								onInput={(e) => setPassword(e.currentTarget.value)}
							/>
						</label>
						<br />
						<label>
							<div class="label-text">confirm password</div>
							<input
								class="input"
								type="password"
								placeholder="dolphins"
								value={confirmPassword()}
								onInput={(e) => setConfirmPassword(e.currentTarget.value)}
							/>
						</label>
						<br />
						<br />
						<input
							class="button submit-btn"
							type="submit"
							value={"set password"}
						/>
					</form>
				</section>
			</div>
		</Modal>
	);
};
